import { inject, Injectable } from '@angular/core';
import {
  CONTACT_OUTCOME_KINDS,
  CreateInteractionParams,
  CreateInteractionResult,
  Interaction,
  INTERACTION_EDITABLE_FIELDS,
  UpdateInteractionParams,
} from '../models/interaction.model';
import { AddressService } from './address.service';
import { AuthService } from './auth.service';
import { ChangeLogService } from './change-log.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Offline-first interaction log. Writing an interaction has two side effects
 * on its address, both handled here so callers can't forget them:
 *
 * - the denormalized `last_interaction_at` / `last_outcome_kind` summary
 *   that the list (and later the map) colours from, and
 * - the do-not-contact flag when `outcome_kind === 'do_not_contact'`.
 *
 * Logging a contact against a DNC-flagged address is a business-rule
 * failure, returned as a structured result rather than thrown.
 */
@Injectable({
  providedIn: 'root',
})
export class InteractionService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);
  private addressService = inject(AddressService);
  private changeLog = inject(ChangeLogService);
  private logger = inject(LoggerService).getLogger('interaction');

  /** A single interaction by ID. Returns `null` if not found or deleted. */
  async getById(id: string): Promise<Interaction | null> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT * FROM interactions WHERE id = ? AND deleted_at IS NULL',
      [id],
    );
    return rows.length > 0 ? (rows[0] as Interaction) : null;
  }

  /**
   * Interactions for one address, newest first. With `onlyUserId` set, only
   * that user's rows are returned (for users without `interaction.read-others`).
   */
  async getForAddress(addressId: string, onlyUserId?: string): Promise<Interaction[]> {
    await this.db.init();
    const rows = onlyUserId
      ? await this.db.exec(
          `SELECT * FROM interactions WHERE address_id = ? AND created_by = ? AND deleted_at IS NULL
           ORDER BY occurred_at DESC`,
          [addressId, onlyUserId],
        )
      : await this.db.exec(
          `SELECT * FROM interactions WHERE address_id = ? AND deleted_at IS NULL
           ORDER BY occurred_at DESC`,
          [addressId],
        );
    return rows as Interaction[];
  }

  /** One user's interactions within [from, to), newest first. */
  async getForUserBetween(userId: string, from: number, to: number): Promise<Interaction[]> {
    await this.db.init();
    const rows = await this.db.exec(
      `SELECT * FROM interactions
       WHERE created_by = ? AND occurred_at >= ? AND occurred_at < ? AND deleted_at IS NULL
       ORDER BY occurred_at DESC`,
      [userId, from, to],
    );
    return rows as Interaction[];
  }

  /** One user's interactions with a follow-up due before `before`, soonest first. */
  async getFollowUpsDue(userId: string, before: number): Promise<Interaction[]> {
    await this.db.init();
    const rows = await this.db.exec(
      `SELECT * FROM interactions
       WHERE created_by = ? AND follow_up_at IS NOT NULL AND follow_up_at < ? AND deleted_at IS NULL
       ORDER BY follow_up_at ASC`,
      [userId, before],
    );
    return rows as Interaction[];
  }

  createQueryForAddress(addressId: string, onlyUserId?: string): QuerySignal<Interaction[]> {
    return this.db.createQuery('interactions', () => this.getForAddress(addressId, onlyUserId));
  }

  createQueryById(id: string): QuerySignal<Interaction | null> {
    return this.db.createQuery('interactions', () => this.getById(id), null);
  }

  createQueryForUserBetween(userId: string, from: number, to: number): QuerySignal<Interaction[]> {
    return this.db.createQuery('interactions', () => this.getForUserBetween(userId, from, to));
  }

  createQueryFollowUpsDue(userId: string, before: number): QuerySignal<Interaction[]> {
    return this.db.createQuery('interactions', () => this.getFollowUpsDue(userId, before));
  }

  /** Log an interaction. See class docs for side effects and the DNC rule. */
  async create(params: CreateInteractionParams): Promise<CreateInteractionResult> {
    const userId = this.requireUser('log');
    await this.db.init();

    const address = await this.addressService.getById(params.address_id);
    if (!address) {
      return { success: false, reason: 'address_not_found' };
    }

    const isContact = CONTACT_OUTCOME_KINDS.includes(params.outcome_kind);
    if (address.do_not_contact && isContact && !params.override_do_not_contact) {
      this.logger.info('Refused interaction against DNC address:', params.address_id);
      return { success: false, reason: 'do_not_contact' };
    }

    const id = this.db.generateId();
    const now = this.clockSync.now();
    const occurredAt = params.occurred_at ?? now;

    this.logger.debug('Logging interaction:', {
      id,
      address_id: params.address_id,
      outcome_kind: params.outcome_kind,
    });

    await this.db.run(
      `INSERT INTO interactions (
         id, address_id, interaction_type, outcome_kind, objection_id, occurred_at, duration_seconds,
         notes, captured_lat, captured_lng, captured_accuracy, follow_up_at,
         created_by, created_at, updated_by, updated_at
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id,
        params.address_id,
        params.interaction_type,
        params.outcome_kind,
        params.outcome_kind === 'objection' ? (params.objection_id ?? '') : '',
        occurredAt,
        params.duration_seconds ?? 0,
        params.notes ?? '',
        params.captured_lat ?? null,
        params.captured_lng ?? null,
        params.captured_accuracy ?? null,
        params.follow_up_at ?? null,
        userId,
        now,
        userId,
        now,
      ],
    );

    await this.addressService.applyInteractionSummary(
      params.address_id,
      occurredAt,
      params.outcome_kind,
      params.outcome_kind === 'objection' ? (params.objection_id ?? '') : '',
    );
    if (params.outcome_kind === 'do_not_contact' && !address.do_not_contact) {
      await this.addressService.setDoNotContact(params.address_id, params.notes ?? '');
    }

    const created = await this.getById(id);
    if (!created) throw new Error('Failed to retrieve interaction after creation');
    return { success: true, interaction: created };
  }

  /**
   * Edit an interaction (typo window). Omitted fields keep their value. Writes
   * a change_log entry and recomputes the address summary if the outcome or
   * time moved.
   */
  async update(params: UpdateInteractionParams): Promise<Interaction> {
    const userId = this.requireUser('update');
    await this.db.init();
    const existing = await this.getById(params.id);
    if (!existing) throw new Error(`Interaction '${params.id}' not found`);

    const next: Interaction = { ...existing };
    for (const field of INTERACTION_EDITABLE_FIELDS) {
      const value = params[field];
      if (value !== undefined) {
        Object.assign(next, { [field]: value });
      }
    }
    if (next.outcome_kind !== 'objection') next.objection_id = '';

    const changes = this.changeLog.diff(existing, next, INTERACTION_EDITABLE_FIELDS);
    if (!changes) return existing;

    const now = this.clockSync.now();
    const setClause = INTERACTION_EDITABLE_FIELDS.map((f) => `${f} = ?`).join(', ');
    await this.db.run(
      `UPDATE interactions SET ${setClause}, updated_by = ?, updated_at = ? WHERE id = ?`,
      [...INTERACTION_EDITABLE_FIELDS.map((f) => next[f]), userId, now, params.id],
    );
    await this.changeLog.record('interaction', params.id, existing, next, INTERACTION_EDITABLE_FIELDS);

    if ('outcome_kind' in changes || 'occurred_at' in changes || 'objection_id' in changes) {
      await this.addressService.recomputeInteractionSummary(existing.address_id);
    }

    const updated = await this.getById(params.id);
    if (!updated) throw new Error('Failed to retrieve interaction after update');
    return updated;
  }

  /** Soft-delete and recompute the address summary from what remains. */
  async delete(id: string): Promise<void> {
    const userId = this.requireUser('delete');
    await this.db.init();
    const existing = await this.getById(id);
    if (!existing) return;

    const now = this.clockSync.now();
    await this.db.run(
      'UPDATE interactions SET deleted_at = ?, updated_by = ?, updated_at = ? WHERE id = ?',
      [now, userId, now, id],
    );
    await this.addressService.recomputeInteractionSummary(existing.address_id);
    this.logger.debug('Interaction deleted:', id);
  }

  private requireUser(action: string): string {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error(`User must be authenticated to ${action} an interaction`);
    }
    return userId;
  }
}
